import { Controller } from "@hotwired/stimulus";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

// Connects to data-controller="location-picker"
export default class extends Controller {
  static targets = ["map", "latitude", "longitude", "address", "placeName"];
  static values = { apiKey: String };

  async connect() {
    setOptions({ key: this.apiKeyValue, version: "weekly" });
    await this.initMap();
  }

  async initMap() {
    try {
      const { Map } = await importLibrary("maps");
      const { AdvancedMarkerElement } = await importLibrary("marker");
      const { Autocomplete } = await importLibrary("places");
      const { Geocoder } = await importLibrary("geocoding");

      // 1. Determine Starting Position (Srinagar default, or existing DB coordinates)
      let defaultLat = 34.08;
      let defaultLng = 74.8;

      const savedLat = parseFloat(this.latitudeTarget.value);
      const savedLng = parseFloat(this.longitudeTarget.value);

      if (!isNaN(savedLat) && !isNaN(savedLng)) {
        defaultLat = savedLat;
        defaultLng = savedLng;
      }

      const position = { lat: defaultLat, lng: defaultLng };

      // 2. Draw the Map
      this.map = new Map(this.mapTarget, {
        center: position,
        zoom: 15,
        mapId: "business_address_picker", // Required for Advanced Markers
        mapTypeControl: false,
        streetViewControl: false,
      });

      // 3. Drop the Draggable Pin
      this.marker = new AdvancedMarkerElement({
        map: this.map,
        position: position,
        gmpDraggable: true, // Allows the user to move the pin!
        title: "Your Business Location",
      });

      // 4. Setup Google Places Autocomplete on the text input
      this.autocomplete = new Autocomplete(this.addressTarget);
      this.autocomplete.bindTo("bounds", this.map);

      // --- EVENT LISTENERS ---
      this.addressTarget.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
        }
      });

      // A. When user types an address and selects it from the dropdown
      this.autocomplete.addListener("place_changed", () => {
        const place = this.autocomplete.getPlace();
        if (!place.geometry || !place.geometry.location) return;

        // Move map and marker
        this.map.setCenter(place.geometry.location);
        this.map.setZoom(17);
        this.marker.position = place.geometry.location;

        // Update hidden form fields for Rails
        this.updateCoordinates(
          place.geometry.location.lat(),
          place.geometry.location.lng(),
        );

        // Auto-fill the business name if Google knows it
        if (
          place.name &&
          this.hasPlaceNameTarget &&
          this.placeNameTarget.value === ""
        ) {
          this.placeNameTarget.value = place.name;
        }
      });

      // B. When user drags the pin physically on the map
      this.marker.addListener("dragend", (event) => {
        // AdvancedMarkerElement dragend event returns the new latLng in event.latLng
        const newLat = event.latLng.lat();
        const newLng = event.latLng.lng();

        this.updateCoordinates(newLat, newLng);

        // Convert the new coordinates back into a street address for the text input
        const geocoder = new Geocoder();
        geocoder.geocode(
          { location: { lat: newLat, lng: newLng } },
          (results, status) => {
            if (status === "OK" && results[0]) {
              this.addressTarget.value = results[0].formatted_address;
            }
          },
        );
      });
    } catch (error) {
      console.error("Error initializing Location Picker map:", error);
    }
  }

  updateCoordinates(lat, lng) {
    if (this.hasLatitudeTarget) this.latitudeTarget.value = lat;
    if (this.hasLongitudeTarget) this.longitudeTarget.value = lng;
  }
}
